// Trusted-device "quick login" for teachers and school admins.
//
// Lets a teacher or admin who has opted in return to the login screen after
// logging out and sign back in with a single tap — no email, no password — for
// as long as they stay on the same browser + device. We cache each account's
// Supabase *refresh token* in localStorage (the same secret Supabase's own
// persisted session already keeps there) alongside just enough identity to
// render a chooser card, and restore the session on tap via refreshSession().
//
// It is strictly opt-in and per-device: enabling is offered via a prompt (at
// conventional login and at logout), the prompt can be dismissed permanently,
// and the whole feature can be switched off from the teacher account menu.
// Never active in demo mode.
//
// Security note: the refresh token in localStorage is the same exposure as
// Supabase's default persisted session — the only differences are that it is
// gated behind an explicit card tap and that it deliberately survives an
// explicit logout (that IS the trusted-device promise the teacher opted into).

import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { isDemoMode } from './demo/store';

// TEMPORARY diagnostic logging (2026-07) — tracing why cached one-tap tokens
// are rejected on restore. Remove once the refresh-token issue is understood.
const qlog = (...a: unknown[]) => { try { console.warn('[quick-login]', ...a); } catch { /* ignore */ } };
const tokPrefix = (t?: string) => (t ? `${t.slice(0, 10)}…(${t.length})` : '(none)');

export type QuickLoginRole = 'teacher' | 'school_admin';

export interface RememberedAccount {
  userId: string;
  email: string;
  name: string;
  /** Which portal this account belongs to — used to show the right cards on
   *  the teacher vs school login page. Legacy accounts (pre-admin support)
   *  had no role and are treated as 'teacher'. */
  role: QuickLoginRole;
  method: 'password' | 'microsoft';
  refreshToken: string;
  savedAt: number;
}

/** Roles eligible for quick login. Cover teachers are excluded (one-time token,
 *  no Supabase session to cache). */
function isEligibleRole(role: unknown): role is QuickLoginRole {
  return role === 'teacher' || role === 'school_admin';
}

const ACCOUNTS_KEY  = 'cmc_quick_login_accounts';
const ENABLED_KEY   = 'cmc_quick_login_enabled';
const DISMISSED_KEY = 'cmc_quick_login_prompt_dismissed';
const PARKED_KEY    = 'cmc_quick_login_parked';

// Cap the chooser so a shared device can't accumulate an unbounded list.
const MAX_ACCOUNTS = 5;

// ── Feature flag (per device) ──────────────────────────────────────────────

export function isQuickLoginEnabled(): boolean {
  if (isDemoMode()) return false;
  try { return localStorage.getItem(ENABLED_KEY) === '1'; } catch { return false; }
}

export function setQuickLoginEnabled(on: boolean): void {
  try {
    if (on) {
      localStorage.setItem(ENABLED_KEY, '1');
    } else {
      localStorage.removeItem(ENABLED_KEY);
      // Turning the feature off must not leave one-tap credentials behind.
      clearRememberedAccounts();
    }
  } catch { /* storage unavailable, feature silently stays off */ }
}

// ── Opt-in prompt gating ───────────────────────────────────────────────────

export function isPromptDismissed(): boolean {
  try { return localStorage.getItem(DISMISSED_KEY) === '1'; } catch { return false; }
}

export function dismissPrompt(): void {
  try { localStorage.setItem(DISMISSED_KEY, '1'); } catch { /* ignore */ }
}

/** The opt-in prompt appears only while the feature is off AND the teacher
 *  hasn't permanently dismissed it. Enabling stops it too (enabled ⇒ off-gate). */
export function shouldShowQuickLoginPrompt(): boolean {
  return !isDemoMode() && !isQuickLoginEnabled() && !isPromptDismissed();
}

// ── "Parked" session marker ────────────────────────────────────────────────
//
// Because opted-in logout no longer revokes the Supabase session (it stays live
// so the one-tap card can resume it), a route that decides admittance from
// supabase.auth.getSession() alone — e.g. the /teacher mount effect — would
// silently re-admit a user who has just explicitly logged out. This flag marks
// "the live session is parked ONLY for the card; do not auto-admit from it." It
// is set at opted-in logout and cleared the moment the user actively signs back
// in (card restore or a fresh login). Login gates must honour it.

export function markSessionParked(): void {
  try { localStorage.setItem(PARKED_KEY, '1'); } catch { /* ignore */ }
}
export function clearSessionParked(): void {
  try { localStorage.removeItem(PARKED_KEY); } catch { /* ignore */ }
}
export function isSessionParked(): boolean {
  try { return localStorage.getItem(PARKED_KEY) === '1'; } catch { return false; }
}

// ── Remembered accounts ────────────────────────────────────────────────────

/** Remembered accounts, optionally filtered to one portal (teacher / admin).
 *  A stored account with no role predates admin support → treated as teacher. */
export function getRememberedAccounts(role?: QuickLoginRole): RememberedAccount[] {
  if (isDemoMode()) return [];
  try {
    const raw = localStorage.getItem(ACCOUNTS_KEY);
    const list = raw ? (JSON.parse(raw) as RememberedAccount[]) : [];
    const valid = Array.isArray(list) ? list.filter(a => a && a.refreshToken && a.email && a.userId) : [];
    return role ? valid.filter(a => (a.role ?? 'teacher') === role) : valid;
  } catch { return []; }
}

function writeAccounts(list: RememberedAccount[]): void {
  try { localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

/** Upsert (by userId) a remembered account, most-recent first. */
export function rememberAccount(acct: RememberedAccount): void {
  if (isDemoMode()) return;
  const rest = getRememberedAccounts().filter(a => a.userId !== acct.userId);
  writeAccounts([acct, ...rest].slice(0, MAX_ACCOUNTS));
}

export function forgetAccount(userId: string): void {
  writeAccounts(getRememberedAccounts().filter(a => a.userId !== userId));
}

export function clearRememberedAccounts(): void {
  try { localStorage.removeItem(ACCOUNTS_KEY); } catch { /* ignore */ }
}

// ── Capture / restore ──────────────────────────────────────────────────────

/** Build a RememberedAccount from a live Supabase session. Returns null if the
 *  session lacks the pieces we need (email / refresh token). */
export function accountFromSession(session: Session, method: 'password' | 'microsoft'): RememberedAccount | null {
  const user = session.user;
  const refreshToken = session.refresh_token;
  if (!user?.email || !refreshToken) return null;
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  const name =
    ((meta.full_name as string | undefined)
      ?? (meta.coverTeacherName as string | undefined)
      ?? `${(meta.firstName as string) ?? ''} ${(meta.lastName as string) ?? ''}`.trim())
    || user.email.split('@')[0];
  const role: QuickLoginRole = meta.role === 'school_admin' ? 'school_admin' : 'teacher';
  return { userId: user.id, email: user.email, name, role, method, refreshToken, savedAt: Date.now() };
}

/** If enabled, snapshot the CURRENT Supabase session as a remembered account.
 *  Safe no-op when off / demo / ineligible role / no session. */
export async function rememberCurrentSession(method: 'password' | 'microsoft' = 'password'): Promise<void> {
  if (!isQuickLoginEnabled()) return;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    if (!isEligibleRole(session.user.user_metadata?.role)) return;
    const acct = accountFromSession(session, method);
    if (acct) rememberAccount(acct);
  } catch { /* ignore */ }
}

/**
 * Logout hook (teachers + admins). When quick-login is on, snapshot the freshest
 * refresh token (the one about to be discarded) and sign out with scope:'local' —
 * that clears the active session locally WITHOUT revoking the refresh token server
 * side, so the chooser card can still restore it. Returns true when it handled
 * the sign-out (caller should skip its own); false (no-op) when off / demo.
 */
export async function quickLoginBeforeLogout(): Promise<boolean> {
  if (isDemoMode() || !isQuickLoginEnabled()) { qlog('logout-capture: skipped (demo or disabled)'); return false; }
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const role = session?.user.user_metadata?.role;
    qlog('logout-capture: getSession →', { hasSession: !!session, role, refreshToken: tokPrefix(session?.refresh_token) });
    if (session && isEligibleRole(role)) {
      const known = getRememberedAccounts().find(a => a.userId === session.user.id);
      const acct = accountFromSession(session, known?.method ?? 'password');
      if (acct) { rememberAccount(acct); qlog('logout-capture: cached account', { email: acct.email, role: acct.role }); }
      else qlog('logout-capture: accountFromSession returned null (no email/token?)');
    } else {
      qlog('logout-capture: not cached (no session or ineligible role)', { role });
    }
    // Deliberately DO NOT sign out. supabase-js signOut() — even scope:'local'
    // — calls /logout and REVOKES the current session's refresh token server
    // side (→ refresh_token_not_found on restore). The trusted-device promise
    // is that the session stays on the device; the caller clears the app's own
    // login state (sessionStorage tokens + onboarding flag) to show the login
    // screen, and the card resumes the still-live session.
    //
    // Park the session so a getSession()-based login gate (the /teacher mount)
    // can't silently re-admit this just-logged-out user — they must tap the card
    // or sign in again. Cleared on the next active sign-in.
    markSessionParked();
    qlog('logout-capture: session left intact + parked for one-tap resume');
    return true;
  } catch (e) {
    qlog('logout-capture: threw', e);
    return false;
  }
}

/**
 * Restore a remembered account's session from its refresh token. On success
 * returns the fresh Session (and re-caches the rotated refresh token so the
 * next tap still works). On failure the stale account is forgotten and null
 * is returned so the caller can fall back to the normal form.
 */
export async function restoreRememberedAccount(acct: RememberedAccount): Promise<Session | null> {
  qlog('restore: attempt', { email: acct.email, role: acct.role, tokenAgeSec: Math.round((Date.now() - acct.savedAt) / 1000) });
  try {
    // Preferred path: the session is still live (we don't revoke on quick-login
    // logout), so just resume it — no stale/rotated/revoked-token risk.
    const { data: { session: live } } = await supabase.auth.getSession();
    if (live && live.user?.id === acct.userId) {
      qlog('restore: OK, resumed live session for', acct.email);
      clearSessionParked(); // user is actively signing back in
      const next = accountFromSession(live, acct.method);
      if (next) rememberAccount(next);
      return live;
    }
    // Fallback: no live session for this account (cleared elsewhere) → try the
    // cached refresh token.
    qlog('restore: no live session, falling back to cached refresh token');
    const { data, error } = await supabase.auth.refreshSession({ refresh_token: acct.refreshToken });
    if (error || !data.session) {
      qlog('restore: FAILED, refreshSession rejected the token', {
        message: error?.message,
        status: (error as { status?: number } | null)?.status,
        code: (error as { code?: string } | null)?.code,
        name: error?.name,
      });
      forgetAccount(acct.userId);
      return null;
    }
    qlog('restore: OK, session re-established via refresh token for', acct.email);
    clearSessionParked(); // user is actively signing back in
    const next = accountFromSession(data.session, acct.method);
    if (next) rememberAccount(next);
    return data.session;
  } catch (e) {
    qlog('restore: threw', e);
    forgetAccount(acct.userId);
    return null;
  }
}
